import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { Wifi, WifiOff } from 'react-bootstrap-icons';
import {
  loadProfitPlus2k8ReferenceTable,
  subscribeToProfitPlus2k8BridgeStatus,
  subscribeToProfitPlus2k8IntegrationConfig,
  resetProfitPlus2k8InitialSetup,
  ProfitPlus2k8BridgeStatus,
  isProfitPlus2k8SyncActivated,
} from '../../../api/configuration';
import type { ProfitPlus2k8OrgConfig } from '../../../models/configuration';
import ProfitPlus2k8SetupModal from '../profitplus/ProfitPlus2k8SetupModal';

interface ProfitPlus2k8BridgeBannerProps {
  organizationId: string | null;
}

const DEFAULT_BRIDGE_STATUS: ProfitPlus2k8BridgeStatus = {
  connected: false,
  lastSeenAt: null,
};

function sucursalLabel(
  orderExportAlmacen: string,
  almacenes: Array<{ co_alma?: string; alma_des?: string }> | null | undefined
): string {
  const code = (orderExportAlmacen || '').trim();
  if (almacenes?.length) {
    const found = almacenes.find(
      (a: { co_alma?: string }) => String(a.co_alma ?? '').trim() === code
    );
    const des = (found as { alma_des?: string } | undefined)?.alma_des;
    if (des && String(des).trim()) return `${code} — ${String(des).trim()}`;
  }
  return code;
}

const ProfitPlus2k8BridgeBanner: React.FC<ProfitPlus2k8BridgeBannerProps> = ({
  organizationId,
}) => {
  const [showBanner, setShowBanner] = useState(false);
  const [config, setConfig] = useState<ProfitPlus2k8OrgConfig | null>(null);
  const [status, setStatus] = useState<ProfitPlus2k8BridgeStatus>(
    DEFAULT_BRIDGE_STATUS
  );
  const [almacenes, setAlmacenes] = useState<Array<{
    co_alma?: string;
    alma_des?: string;
  }> | null>(null);
  const [resetting, setResetting] = useState(false);
  /** Wizard open; optional import steps can continue after paso 1 is saved. */
  const [profitSetupModalOpen, setProfitSetupModalOpen] = useState(false);
  /** User closed the modal before paso 1 — do not auto-reopen until they choose to continue. */
  const dismissedIncompleteRef = useRef(false);
  /** User explicitly opened the wizard (continue / re-run); do not auto-close on paso 1 save. */
  const manualSetupOpenRef = useRef(false);

  const initialSetupCompleted = Boolean(config?.initialSetupCompleted);

  const bridgeUnsubRef = useRef<(() => void) | null>(null);
  const integrationUnsubRef = useRef<(() => void) | null>(null);
  const wasSyncActiveRef = useRef(false);

  useEffect(() => {
    wasSyncActiveRef.current = false;
    bridgeUnsubRef.current?.();
    bridgeUnsubRef.current = null;
    integrationUnsubRef.current?.();
    integrationUnsubRef.current = null;

    if (!organizationId) {
      setShowBanner(false);
      setConfig(null);
      setStatus(DEFAULT_BRIDGE_STATUS);
      setAlmacenes(null);
      return;
    }

    integrationUnsubRef.current = subscribeToProfitPlus2k8IntegrationConfig(
      organizationId,
      pp => {
        setConfig(pp);
        const active = isProfitPlus2k8SyncActivated(pp);
        const wasActive = wasSyncActiveRef.current;
        wasSyncActiveRef.current = active;

        if (!active) {
          setShowBanner(false);
          bridgeUnsubRef.current?.();
          bridgeUnsubRef.current = null;
          setStatus(DEFAULT_BRIDGE_STATUS);
          setAlmacenes(null);
          return;
        }

        setShowBanner(true);

        if (!bridgeUnsubRef.current) {
          bridgeUnsubRef.current = subscribeToProfitPlus2k8BridgeStatus(
            organizationId,
            s => setStatus(s),
            () => setStatus(DEFAULT_BRIDGE_STATUS)
          );
        }

        if (!wasActive) {
          loadProfitPlus2k8ReferenceTable(organizationId, 'almacenes')
            .then(rows =>
              setAlmacenes(
                rows as Array<{ co_alma?: string; alma_des?: string }>
              )
            )
            .catch(() => setAlmacenes(null));
        }
      }
    );

    return () => {
      integrationUnsubRef.current?.();
      integrationUnsubRef.current = null;
      bridgeUnsubRef.current?.();
      bridgeUnsubRef.current = null;
    };
  }, [organizationId]);

  useEffect(() => {
    if (initialSetupCompleted) {
      dismissedIncompleteRef.current = false;
    }
  }, [initialSetupCompleted]);

  useEffect(() => {
    if (!organizationId || !showBanner) return;
    if (!status.connected || !config || !isProfitPlus2k8SyncActivated(config))
      return;
    if (initialSetupCompleted) return;
    if (dismissedIncompleteRef.current) return;
    setProfitSetupModalOpen(true);
  }, [
    organizationId,
    showBanner,
    status.connected,
    initialSetupCompleted,
    config?.enabled,
    config?.syncType,
  ]);

  const handleProfitSetupModalClose = useCallback(() => {
    manualSetupOpenRef.current = false;
    setProfitSetupModalOpen(false);
    if (!initialSetupCompleted) {
      dismissedIncompleteRef.current = true;
    }
  }, [initialSetupCompleted]);

  const handleOpenProfitSetupModal = useCallback(() => {
    dismissedIncompleteRef.current = false;
    manualSetupOpenRef.current = true;
    setProfitSetupModalOpen(true);
  }, []);

  const sucursalDisplay = useMemo(() => {
    const code = (config?.orderExportAlmacen ?? '').trim();
    if (!code) return '—';
    return sucursalLabel(code, almacenes);
  }, [config?.orderExportAlmacen, almacenes]);

  const handleRestartSetup = async () => {
    if (!organizationId || resetting) return;
    setResetting(true);
    try {
      await resetProfitPlus2k8InitialSetup(organizationId);
      dismissedIncompleteRef.current = false;
      manualSetupOpenRef.current = true;
      setProfitSetupModalOpen(true);
    } finally {
      setResetting(false);
    }
  };

  if (!showBanner) return null;

  const showProfitSetupModal =
    organizationId != null && profitSetupModalOpen && Boolean(status.connected);

  return (
    <>
      {showProfitSetupModal && organizationId && (
        <ProfitPlus2k8SetupModal
          show={showProfitSetupModal}
          organizationId={organizationId}
          status={status}
          onClose={handleProfitSetupModalClose}
        />
      )}
      {status.connected ? (
        <div
          className="d-inline-flex flex-column align-items-start gap-1 mb-2 py-2 px-3 rounded border border-success border-opacity-25 bg-success bg-opacity-10 small flex-wrap"
          role="status"
        >
          <div className="d-flex align-items-center flex-wrap gap-2">
            <Wifi
              className="flex-shrink-0 text-success"
              style={{ fontSize: '0.85rem' }}
              aria-hidden
            />
            <span className="text-success-emphasis">
              Profit Plus 2k8 · Puente conectado
              {status.user ? ` (${status.user})` : ''}
            </span>
            {!initialSetupCompleted &&
              !profitSetupModalOpen &&
              organizationId && (
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 text-decoration-none"
                  onClick={handleOpenProfitSetupModal}
                >
                  Continuar configuración inicial
                </button>
              )}
            {initialSetupCompleted && organizationId && (
              <button
                type="button"
                className="btn btn-link btn-sm p-0 ms-1 text-decoration-none"
                onClick={handleRestartSetup}
                disabled={resetting}
              >
                {resetting ? '…' : 'Volver a ejecutar configuración inicial'}
              </button>
            )}
          </div>
          {config && (
            <div
              className="d-flex align-items-center flex-wrap gap-3 text-muted"
              style={{ fontSize: '0.8rem' }}
            >
              <span title="Sucursal / Almacén para pedidos">
                <strong className="text-body-secondary">Sucursal:</strong>{' '}
                {sucursalDisplay}
              </span>
              <span title="Moneda por defecto para exportación de pedidos">
                <strong className="text-body-secondary">Moneda:</strong>{' '}
                {config.orderExportDefaultMoneda?.trim()
                  ? config.orderExportDefaultMoneda
                  : '—'}
              </span>

              {config.lastSyncAt && (
                <span title="Última sincronización">
                  <strong className="text-body-secondary">Últ. sync:</strong>{' '}
                  {new Date(config.lastSyncAt).toLocaleString(undefined, {
                    dateStyle: 'short',
                    timeStyle: 'short',
                  })}
                </span>
              )}
              {status.availableCounts && (
                <span className="opacity-75">
                  Vendedores: {status.availableCounts.salesPeople ?? 0} ·
                  Clientes: {status.availableCounts.clients ?? 0} · Productos:{' '}
                  {status.availableCounts.products ?? 0}
                </span>
              )}
            </div>
          )}
        </div>
      ) : (
        <div
          className="d-inline-flex flex-column align-items-start gap-1 mb-2 py-2 px-3 rounded border border-opacity-25 small text-muted"
          style={{
            borderColor: 'var(--bs-secondary)',
            backgroundColor: 'var(--bs-secondary-bg)',
          }}
          role="status"
        >
          <div className="d-flex align-items-center gap-2">
            <WifiOff
              className="flex-shrink-0"
              style={{ fontSize: '0.85rem' }}
              aria-hidden
            />
            <span>Profit Plus 2k8 · Puente no conectado</span>
          </div>
          {config && (
            <div
              className="d-flex align-items-center flex-wrap gap-3 opacity-75"
              style={{ fontSize: '0.8rem' }}
            >
              <span>Sucursal: {sucursalDisplay}</span>
              <span>
                Moneda:{' '}
                {config.orderExportDefaultMoneda?.trim()
                  ? config.orderExportDefaultMoneda
                  : '—'}
              </span>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ProfitPlus2k8BridgeBanner;
